import React, {createContext, useCallback, useContext, useEffect, useRef, useState} from 'react'
import io from 'socket.io-client'

import useSound from '../Components/Sound/useSound'
import {toAbsoluteUrl} from '../../../../../_heomoney/helpers'
import axios from 'axios'
import { useAuth } from '../../../auth'

export interface MessageModel {
  id?: number
  uniqueKey?: string
  user: number
  type: 'in' | 'out'
  text: string
  time: string
  timestamp?: string
  date?: string
  file_url?: string | null
  typeMessage?: string | null
}

interface Contact {
  name: string
  email: string
  phone: string
  avatar: string
  idUsuario: number
  unreadMessages: number
  createdAt: string
  chats?: [
    {
      id: number
      message: string
      timestamp: string
      role: string
    }
  ]
}

interface ChatSocketContextProps {
  socket: any | null
  contacts: Contact[]
  setContacts: React.Dispatch<React.SetStateAction<Contact[]>>
  selectedContact: {name: string; phone: string; idUsuario: number} | null
  setSelectedContact: React.Dispatch<
    React.SetStateAction<{
      name: string
      phone: string
      idUsuario: number
    } | null>
  >
  messages: MessageModel[]
  setMessages: React.Dispatch<React.SetStateAction<MessageModel[]>>
  contentMessages: React.RefObject<HTMLDivElement>
  scrollToBottomTimeoutRef: React.MutableRefObject<NodeJS.Timeout | null>
  lastScrollHeight: React.MutableRefObject<number>
  initialLoadComplete: boolean
  setInitialLoadComplete: React.Dispatch<React.SetStateAction<boolean>>
  newContact: (phone: string) => void
  scrollToBottom: (force?: boolean) => void
  formatMessageDate: (timestamp: string) => string
  loadContacts: () => Promise<void>
  ListMessages: () => Promise<void>
  isInitialLoad: boolean
  setIsInitialLoad: React.Dispatch<React.SetStateAction<boolean>>
  isLoadingMore: boolean
  setIsLoadingMore: React.Dispatch<React.SetStateAction<boolean>>
  isLoadingContacts: boolean
  setIsLoadingContacts: React.Dispatch<React.SetStateAction<boolean>>
}

const ChatSocketContext = createContext<ChatSocketContextProps | null>(null)

export const ChatSocketProvider = ({children}: {children: React.ReactNode}) => {
  const API_URL_BOT = process.env.REACT_APP_API_URL_WEB

  const {playSound} = useSound(toAbsoluteUrl('/media/audio/whatsapp.mp3'))

  const {currentUser} = useAuth()

  // constantes de socket
  const [socket, setSocket] = useState<any | null>(null)

  // constantes de chat
  const [selectedContact, setSelectedContact] = useState<{
    name: string
    phone: string
    idUsuario: number
  } | null>(null)

  const [contacts, setContacts] = useState<Contact[]>([])

  const [messages, setMessages] = useState<MessageModel[]>([])

  const contentMessages = useRef<HTMLDivElement>(null)

  const lastScrollHeight = useRef<number>(0)

  const [initialLoadComplete, setInitialLoadComplete] = useState<boolean>(false)

  const [isInitialLoad, setIsInitialLoad] = useState<boolean>(true)

  const scrollToBottomTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false)

  const [isLoadingContacts, setIsLoadingContacts] = useState<boolean>(false)

  // functions
  const scrollToBottom = useCallback(
    (force: boolean = false) => {
      if (contentMessages.current && (force || initialLoadComplete)) {
        // Limpiar timeout anterior si existe
        if (scrollToBottomTimeoutRef.current) {
          clearTimeout(scrollToBottomTimeoutRef.current)
        }

        // Usar múltiples intentos para asegurar el scroll
        const attemptScroll = (attempts: number = 0) => {
          if (attempts >= 5) return // Máximo 5 intentos

          if (contentMessages.current) {
            const {scrollHeight} = contentMessages.current
            contentMessages.current.scrollTop = scrollHeight

            // Verificar si realmente llegó al final, si no, intentar de nuevo
            scrollToBottomTimeoutRef.current = setTimeout(() => {
              if (
                contentMessages.current &&
                contentMessages.current.scrollTop < scrollHeight - 10
              ) {
                attemptScroll(attempts + 1)
              }
            }, 50)
          }
        }

        attemptScroll()
      }
    },
    [initialLoadComplete]
  )

  const formatMessageDate = (timestamp: string) => {
    const messageDate = new Date(timestamp)
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(yesterday.getDate() - 1)

    const isToday = messageDate.toDateString() === today.toDateString()
    const isYesterday = messageDate.toDateString() === yesterday.toDateString()

    if (isToday) {
      return 'Hoy'
    } else if (isYesterday) {
      return 'Ayer'
    } else {
      return messageDate.toLocaleDateString('es-ES', {
        weekday: 'long',
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    }
  }

  const newContact = (phone: string) => {
    let contactFound = false

    setContacts((prevContacts) => {
      const updatedContacts = prevContacts.map((contact) => {
        if (contact.phone.trim() === phone.trim()) {
          contactFound = true
          return {...contact}
        }
        return contact
      })

      if (!contactFound) {
        console.log('Añadiendo nuevo contacto:', phone.trim())
        updatedContacts.push({
          name: phone.trim(),
          email: 'No email',
          phone: phone.trim(),
          avatar: '/media/avatars/300-25.jpg',
          idUsuario: 0,
          unreadMessages: 0,
          createdAt: new Date().toISOString(),
        })
      }

      return updatedContacts
    })
  }

  const loadContacts = async () => {
    try {
      setIsLoadingContacts(true)
      const response = await axios.post(`${API_URL_BOT}/openai/get_contact_info`)
      const data = response.data

      // console.log(data)

      const formatted = data.map((contact: any) => ({
        name: contact.name || contact.phone,
        email: contact.email || 'No email',
        phone: contact.phone,
        avatar: '/media/avatars/300-25.jpg',
        idUsuario: contact.idUsuario || 0,
        unreadMessages: contact.unread_messages,
        createdAt: contact.createdAt || new Date().toISOString(),
        chats: contact.chats || [],
      }))

      // Fusionar sin duplicar teléfonos ya existentes
      setContacts(
        // (prev) => {
        // const phones = new Set(prev.map((c) => c.phone))
        // const merged = [...prev]

        // for (const contact of formatted) {
        //   if (!phones.has(contact.phone)) {
        //     merged.push(contact)
        //   }
        // }

        // return merged

        () => {
          let m: any = []
          for (const contact of formatted) {
            m.push(contact)
          }
          return m
        }
      )

      setIsLoadingContacts(false)
    } catch (error) {
      setIsLoadingContacts(false)
      console.error('Error fetching contacts:', error)
    }
  }

  const loadMessagesForWeek = async (page: number) => {
    try {
      console.log('Cargando mensajes para semana:', page)

      if (!selectedContact?.phone) {
        console.log('No hay número seleccionado, no se cargan mensajes.')
        return []
      }
      const res = await axios.post(`${API_URL_BOT}/openai/messages_by_page`, {
        phone: selectedContact?.phone,
        page,
      })

      const messagesFromServer = res.data.messages.map((msg: any, index: number) => ({
        id: msg.id,
        uniqueKey: `${page}-${msg.id}-${msg.timestamp}-${index}`, // Key único para React
        user: msg.role === 'user' ? 2 : 4,
        type: msg.role === 'user' ? 'in' : 'out',
        text: msg.message,
        timestamp: msg.timestamp,
        date: formatMessageDate(msg.timestamp),
        time: new Date(msg.timestamp).toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        }),
        file_url: msg.file_url || null,
        typeMessage: msg.typeMessage || 'conversation',
      }))

      return messagesFromServer
    } catch (error) {
      console.error('Error loading messages:', error)
      return []
    }
  }

  // Función para cargar mensajes iniciales (solo semana actual)
  const ListMessages = async () => {
    try {
      console.log('Cargando mensajes iniciales para:', selectedContact?.phone)
      setIsInitialLoad(true)

      // Solo cargar mensajes de la semana actual (semana 0)
      const initialMessages = await loadMessagesForWeek(0)
      setMessages(initialMessages)

      // Marcar como completada la carga inicial
      setIsInitialLoad(false)
      setInitialLoadComplete(true)

      // Hacer scroll hacia abajo después de un breve delay
      setTimeout(() => {
        scrollToBottom(true)
      }, 400)
    } catch (error) {
      console.error('Error loading initial messages:', error)
      setIsInitialLoad(false)
      setInitialLoadComplete(true)
    }
  }

  // useEffects de socket
  useEffect(() => {
    try {
      if (!currentUser) return

      const socketInstance = io(API_URL_BOT!, {transports: ['websocket'], reconnectionAttempts: 3})

      if (socketInstance) {
        setSocket(socketInstance)
        console.log('🟢 Socket conectado')
      }

      return () => {
        if (socketInstance && socketInstance.connected) {
          console.log('🔴 Desconectando socket')
          socketInstance.disconnect()
        }
      }
    } catch (error) {
      console.log(error)
    }
  }, [currentUser])

  // useEffects de chat
  useEffect(() => {
    if (!socket) return

    const handleNotificacion = (data: {phone: string; unreadMessages: number}) => {
      console.log('event notificacion')

      const {phone, unreadMessages} = data

      let contactFound = false

      setContacts((prevContacts) => {
        const updatedContacts = prevContacts.map((contact) => {
          if (contact.phone === phone) {
            contactFound = true
            return {...contact, unreadMessages: unreadMessages}
          } else {
            return contact
          }
        })

        if (!contactFound) {
          console.log('Añadiendo nuevo contacto:', phone.trim())
          updatedContacts.push({
            name: phone.trim(),
            email: 'No email',
            phone: phone.trim(),
            avatar: '/media/avatars/300-25.jpg',
            idUsuario: 0,
            unreadMessages: unreadMessages,
            createdAt: new Date().toISOString(),
          })
        }

        return updatedContacts
      })

      playSound()

      //ordenar contactos, colocamos primero al contacto que recibe un mensaje
      setContacts((prevContacts) => {
        let unshiftContact: Contact | null = null

        let orderedContacts = prevContacts
          .map((prevContact) => {
            if (prevContact.phone !== phone) {
              return prevContact
            } else {
              unshiftContact = prevContact
              return undefined
            }
          })
          .filter((c): c is Contact => Boolean(c))

        if (unshiftContact) {
          orderedContacts.unshift(unshiftContact)
        }

        return orderedContacts
      })
    }

    socket.on('notificacion', handleNotificacion)

    const handleClearNotificacion = (phone: string) => {
      console.log('event clear notificacion')

      setContacts((prevContacts) =>
        prevContacts.map((contact) => {
          if (contact.phone === phone) {
            return {...contact, unreadMessages: 0}
          } else {
            return contact
          }
        })
      )
    }

    socket.on('clear-notificacion', handleClearNotificacion)

    return () => {
      socket.off('notificacion', handleNotificacion)
      socket.off('clear-notificacion', handleClearNotificacion)
    }
  }, [socket])

  useEffect(() => {
    if (!socket) return

    const handleNewMessage = (data: any) => {
      if (selectedContact && data.from === selectedContact.phone) {
        console.log('event new-message')

        const timestamp = data.timestamp
          ? new Date(data.timestamp * (data.timestamp < 1e12 ? 1000 : 1)).toISOString()
          : new Date().toISOString()

        const newMessage: MessageModel = {
          id: data.id || Date.now(),
          uniqueKey: `socket-${Date.now()}-${Math.random()}`,
          user: data.role === 'user' ? 2 : 4,
          type: data.role === 'user' ? 'in' : 'out',
          text: data.message || data.text || '',
          timestamp,
          date: formatMessageDate(timestamp),
          time: new Date(timestamp).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'}),
          file_url: data.url || '',
          typeMessage: data.typeMessage || '',
        }
        console.log('Nuevo mensaje recibido:', newMessage)

        console.log(newMessage)

        setMessages((prev) => [...prev, newMessage])

        //execute scroll to bottom
        setTimeout(() => scrollToBottom(true), 50)

        //clear notificacion
        setTimeout(() => socket.emit('clear-notificacion', {phone: data.from}), 1000 * 3)
      }

      //ordenar contactos, colocamos primero al contacto que recibe un mensaje
      setContacts((prevContacts) => {
        let unshiftContact: Contact | null = null

        let orderedContacts = prevContacts
          .map((prevContact) => {
            if (prevContact.phone !== data.from) {
              return prevContact
            } else {
              unshiftContact = prevContact
              return undefined
            }
          })
          .filter((c): c is Contact => Boolean(c))

        if (unshiftContact) {
          orderedContacts.unshift(unshiftContact)
        }

        return orderedContacts
      })
    }

    socket.on('new-message', handleNewMessage)

    return () => {
      socket.off('new-message', handleNewMessage)
    }
  }, [socket, selectedContact])

  return (
    <ChatSocketContext.Provider
      value={{
        socket,
        contacts,
        setContacts,
        selectedContact,
        setSelectedContact,
        messages,
        setMessages,
        contentMessages,
        scrollToBottomTimeoutRef,
        lastScrollHeight,
        initialLoadComplete,
        setInitialLoadComplete,
        newContact,
        scrollToBottom,
        formatMessageDate,
        loadContacts,
        ListMessages,
        isInitialLoad,
        setIsInitialLoad,
        isLoadingMore,
        setIsLoadingMore,
        isLoadingContacts,
        setIsLoadingContacts,
      }}
    >
      {children}
    </ChatSocketContext.Provider>
  )
}

export const useChatSocket = () => useContext(ChatSocketContext)
